import moment from "moment-timezone";
import parsePhoneNumber from "libphonenumber-js";
import { ArrowRight } from "lucide-react";
import { CallParty } from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/api/document";

/** A phone number in national format, e.g. "(701) 721-3994". Falls back to the raw text if it can't be parsed. */
export function formatCallPhoneNumber(phoneNumber: string): string {
  return parsePhoneNumber(phoneNumber, "US")?.formatNational() ?? phoneNumber;
}

/** A Loom user shows as their name; an outside caller as a national-format number. */
export function callPartyLabel(party: CallParty | null): string {
  if (!party) return "Unknown";
  if (party.loom_id) return party.user_name || `Loom user ${party.loom_id}`;
  if (party.phone_number) return formatCallPhoneNumber(party.phone_number);
  return party.user_name || "Unknown";
}

interface CallContextLineProps {
  customerName?: string | null;
  callStartedAt?: string | null;
  from?: CallParty | null;
  to?: CallParty | null;
}

/**
 * One line under a call modal's title: customer, who called whom ("From → To"), and when.
 * Each part is left out when it is missing, and the whole line when all are.
 */
export function CallContextLine({ customerName, callStartedAt, from, to }: CallContextLineProps) {
  const hasParties = !!(from || to);
  if (!customerName && !hasParties && !callStartedAt) return null;

  const parts: JSX.Element[] = [];
  if (customerName) {
    parts.push(<span key="customer" className="font-medium text-foreground">{customerName}</span>);
  }
  if (hasParties) {
    parts.push(
      <span key="parties" className="inline-flex items-center gap-1.5" data-testid="call-parties">
        <span className="font-medium" data-testid="call-party-from">{callPartyLabel(from ?? null)}</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-label="to" />
        <span className="font-medium" data-testid="call-party-to">{callPartyLabel(to ?? null)}</span>
      </span>,
    );
  }
  if (callStartedAt) {
    parts.push(
      <span key="time">
        Call{" "}
        <time dateTime={callStartedAt}>
          {moment.tz(callStartedAt, CommonDestinationTimezone).format("MMM Do YYYY, h:mm A z")}
        </time>
      </span>,
    );
  }

  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-muted-foreground" data-testid="todo-context">
      {parts.map((part: JSX.Element, index: number) => (
        <span key={part.key} className="inline-flex items-center gap-1.5">
          {index > 0 && <span aria-hidden="true">·</span>}
          {part}
        </span>
      ))}
    </p>
  );
}
